/**
 * sealed.js — client del sealed-sender (v2).
 *
 * Deposita una busta cifrata nella mailbox del destinatario chiamando l'endpoint
 * HTTP `dropSealed` via fetch SENZA token Firebase → il server non vede né
 * registra il mittente. Il mittente viaggia DENTRO il cifrato ({from,text}) ed è
 * verificato anti-spoof (la box-key dichiarata deve essere quella dell'handshake).
 * Anti-spam: proof-of-work (hashcash) legato a destinatario + busta + finestra.
 *
 * v2.1 = cuore isolato: invio (drop) + apertura del 1° messaggio (prekey, che si
 * decifra senza conoscere il mittente). Routing dei messaggi successivi + UI = v2.2.
 */

import { callFn, getApp, useRelay, RELAY_HOST } from './firebase';
import { initSodium, toBase64Url, fromBase64Url, boxSealAnon, boxSealOpen } from './crypto';
import { serializeHeader, parseHeader } from './ratchet';
import { openOutgoing, openIncoming, encryptMsg, decryptMsg } from './fsSession';
import { saveSession, loadSession, loadMySpk, loadMyPq, peekMyOpk, consumeMyOpk } from './fsStore';
import { addSealedMessage, listSealedPeers, bumpSealedFail, clearSealedFails } from './sealedStore';

const POW_DIFFICULTY_BITS = 18; // ~256k hash attesi
const POW_WINDOW_MS = 600_000;  // 10 minuti
const convKeyOf = (s) => String(s || '').toLowerCase().replace(/^@/, '');
const clone = (o) => (typeof structuredClone === 'function' ? structuredClone(o) : JSON.parse(JSON.stringify(o)));

// Padding identico a fsMessaging: nasconde la lunghezza.
const PAD_BUCKETS = [256, 512, 1024, 2048];
function padText(t) {
  const len = new TextEncoder().encode(t).length;
  const target = PAD_BUCKETS.find((b) => b > len) || (Math.ceil((len + 1) / 1024) * 1024);
  return t + String.fromCharCode(1) + ' '.repeat(Math.max(0, target - len - 1));
}
function unpadText(p) { const i = p.indexOf(String.fromCharCode(1)); return i >= 0 ? p.slice(0, i) : p; }

function leadingZeroBits(bytes) {
  let bits = 0;
  for (const b of bytes) { if (b === 0) { bits += 8; continue; } bits += Math.clz32(b) - 24; break; }
  return bits;
}

/** Proof-of-work: trova nonce s.t. sha256(recipientUid:fsHeader:fsCt:win:nonce)
 *  ha almeno POW_DIFFICULTY_BITS bit a zero iniziali. */
async function computePow(boundContent) {
  const s = await initSodium();
  const win = Math.floor(Date.now() / POW_WINDOW_MS);
  const prefix = `${boundContent}:${win}:`;
  for (let nonce = 0; nonce < 80_000_000; nonce++) {
    const h = s.crypto_hash_sha256(s.from_string(prefix + nonce));
    if (leadingZeroBits(h) >= POW_DIFFICULTY_BITS) return { nonce, ts: Date.now() };
  }
  throw new Error('POW_GIVEUP');
}

function fnUrl(name) {
  // Sotto Tor (useRelay) i fetch GREZZI del sealed-sender DEVONO passare dal relay:
  // Google blocca gli IP di uscita Tor con 403 → "failed to fetch". Il relay
  // inoltra /fn/<name> a cloudfunctions e rimette gli header CORS.
  if (useRelay) return `https://${RELAY_HOST}/fn/${name}`;
  const projectId = getApp().options.projectId;
  return `https://europe-west1-${projectId}.cloudfunctions.net/${name}`;
}

/** Preleva il prekey-bundle del destinatario in modo ANONIMO (onRequest + PoW).
 *  NON usa l'endpoint autenticato: quello rivelerebbe "io voglio scrivere a @x" e,
 *  correlato col deposito subito dopo, smaschererebbe il mittente. */
async function fetchPrekeyBundleAnon(sigil) {
  const sigilLower = String(sigil || '').toLowerCase().replace(/^@/, '');
  const pow = await computePow(`prekey:${sigilLower}`);
  const r = await fetch(fnUrl('fetchPrekeyBundleAnon'), {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ sigil: sigilLower, pow }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || !data.ok) throw new Error(data.error || `PREKEY_HTTP_${r.status}`);
  return data; // { ok, identityKeyBox, identityKeySign, spk, pqPub, opk }
}

/**
 * Deposita un messaggio sealed verso un destinatario. Il mittente (mySigil) è
 * cifrato DENTRO la busta. Lancia 'PEER_PREKEYS_MISSING' se il peer non ha prekey.
 */
export async function dropSealedMessage({ boxKeypair, recipientSigil, recipientUid, mySigil, text, ttl }) {
  if (!text) throw new Error('TEXT_EMPTY');
  if (!recipientUid) throw new Error('RECIPIENT_UID_MISSING');
  const convKey = convKeyOf(recipientSigil);
  let session = await loadSession(convKey);
  let fsPreSealed = null;
  if (!session) {
    // Bundle preso in modo ANONIMO (no auth → niente "A vuole scrivere a @x").
    const bundle = await fetchPrekeyBundleAnon(recipientSigil);
    const o = await openOutgoing({ boxKeypair, peerSigil: recipientSigil, bundle });
    session = o.session;
    // CRITICO: il prekeyMsg contiene ikA = la MIA box-key (identità). NON deve
    // finire in chiaro sul server (lo smaschererebbe via pubKeyBox→sigil). Lo
    // sigilliamo verso la box-key del DESTINATARIO con una sealed box anonima:
    // solo lui può aprirlo, e il ciphertext non rivela il mittente.
    const sealedPre = await boxSealAnon(JSON.stringify(o.prekeyMsg), o.peerBoxPub);
    fsPreSealed = await toBase64Url(sealedPre);
  }
  const working = clone(session);
  // `ttl` (secondi; -1 = ∞) viaggia DENTRO il cifrato → il destinatario applica la
  // stessa autodistruzione dei messaggi normali (scadenza ancorata alla lettura).
  const inner = JSON.stringify({ from: mySigil, text, ttl });
  const m = await encryptMsg(working, padText(inner));
  const fsHeader = await toBase64Url(serializeHeader(m.header));
  const fsCt = await toBase64Url(m.ct);
  const envelope = { v: 3, fsHeader, fsCt, ...(fsPreSealed ? { fsPreSealed } : {}) };
  const pow = await computePow(`${recipientUid}:${fsHeader}:${fsCt}`);

  const r = await fetch(fnUrl('dropSealed'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ recipientUid, envelope, pow }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok || !data.ok) throw new Error(data.error || `DROP_HTTP_${r.status}`);
  await saveSession(convKey, working); // avanza il ratchet solo a deposito riuscito
  return data;
}

export async function fetchSealedMessages() {
  const res = await callFn('fetchSealed')({});
  return res?.data?.items || [];
}

export async function ackSealedMessages(ids) {
  if (!ids?.length) return;
  await callFn('ackSealed')({ ids });
}

/**
 * Apre la PRIMA busta sealed (prekey-message): si decifra SENZA conoscere il
 * mittente (chiavi dentro fsPre). Dentro c'è {from,text}. Anti-spoof: la box-key
 * pubblica del `from` dichiarato DEVE coincidere con quella dell'handshake (ikA),
 * altrimenti il mittente sta mentendo sull'identità.
 * @returns {Promise<{from:string,text:string}>}
 */
export async function openSealedFirst({ boxKeypair, msg }) {
  if (Number(msg?.v) !== 3 || !msg.fsPreSealed) throw new Error('NOT_FIRST_SEALED');
  // Apri la sealed box per recuperare il prekeyMsg (ikA visibile SOLO a noi).
  let fsPre;
  try {
    fsPre = JSON.parse(await boxSealOpen(msg.fsPreSealed, boxKeypair.pub, boxKeypair.priv));
  } catch { throw new Error('SEAL_OPEN_FAILED'); }

  const header = parseHeader(await fromBase64Url(msg.fsHeader));
  const ct = await fromBase64Url(msg.fsCt);
  const mySpk = await loadMySpk(fsPre.spkId);
  if (!mySpk) throw new Error('SPK_NOT_FOUND');
  const myOpk = fsPre.opkId ? await peekMyOpk(fsPre.opkId) : null;
  const myPq = fsPre.pqCt ? await loadMyPq(fsPre.spkId) : null;
  const inc = await openIncoming({ boxKeypair, prekeyMsg: fsPre, mySpk, myOpk, myPq });
  const working = clone(inc.session);
  const innerPadded = await decryptMsg(working, header, ct);
  const inner = JSON.parse(unpadText(innerPadded));

  // Anti-spoof: il decrypt riuscito prova che il mittente controlla la privata di
  // ikA; qui verifichiamo che ikA sia DAVVERO la box-key del sigil dichiarato.
  let verified = false;
  try {
    const f = await callFn('findUserBySigil')({ sigil: inner.from });
    const pubBox = f?.data?.pubKeyBox;
    if (pubBox && fsPre.ikA) {
      const a = await fromBase64Url(pubBox);
      const b = await fromBase64Url(fsPre.ikA);
      verified = a.length === b.length && a.every((x, i) => x === b[i]);
    }
  } catch { /* sotto trattato come spoof */ }
  if (!verified) throw new Error('SENDER_SPOOFED');

  // Anti-replay: se esiste GIÀ una sessione con questo mittente, NON re-inizializzare
  // (un prekey-message ripetuto resetterebbe il ratchet — caso no-OPK). Scarta.
  const convKey = convKeyOf(inner.from);
  if (await loadSession(convKey)) throw new Error('SEALED_REPLAY');

  if (fsPre.opkId) await consumeMyOpk(fsPre.opkId);
  await saveSession(convKey, working); // ancora la sessione al mittente
  return { from: inner.from, text: inner.text, ttl: inner.ttl };
}

/** Apre un messaggio sealed SUCCESSIVO (senza prekey): non sappiamo il mittente,
 *  quindi proviamo le sessioni dei peer noti finché una decifra (il MAC del
 *  ratchet fa da filtro). Operiamo su copia: le sessioni sbagliate non si toccano. */
async function openSealedSubsequent({ msg, knownPeers }) {
  const header = parseHeader(await fromBase64Url(msg.fsHeader));
  const ct = await fromBase64Url(msg.fsCt);
  for (const peer of knownPeers) {
    const convKey = convKeyOf(peer);
    const session = await loadSession(convKey);
    if (!session) continue;
    try {
      const working = clone(session);
      const innerPadded = await decryptMsg(working, header, ct);
      const inner = JSON.parse(unpadText(innerPadded));
      await saveSession(convKey, working); // solo la sessione giusta avanza
      return { from: peer, text: inner.text, ttl: inner.ttl };
    } catch { /* non è questo peer */ }
  }
  throw new Error('NO_MATCHING_SESSION');
}

/** Apre una busta sealed qualsiasi: prekey → riscopre il mittente; ratchet → routing. */
export async function openSealedAny({ boxKeypair, msg, knownPeers }) {
  if (msg.fsPreSealed) return openSealedFirst({ boxKeypair, msg });
  return openSealedSubsequent({ msg, knownPeers });
}

let _sealedSeq = 0;

/** Invia un messaggio sealed a un peer e lo salva nella conversazione locale. */
export async function sendSealed({ boxKeypair, peerSigil, mySigil, text, recipientUid, ttl }) {
  let uid = recipientUid;
  if (!uid) { const f = await callFn('findUserBySigil')({ sigil: peerSigil }); uid = f?.data?.uid; }
  if (!uid) throw new Error('RECIPIENT_NOT_FOUND');
  const r = await dropSealedMessage({ boxKeypair, recipientSigil: peerSigil, recipientUid: uid, mySigil, text, ttl });
  await addSealedMessage(peerSigil, { id: `s_${Date.now()}_${_sealedSeq++}`, mine: true, text, ts: Date.now(), stt: ttl });
  return r;
}

/** Preleva la mailbox, decifra ogni busta, salva nelle conversazioni locali e
 *  ACK solo quelle decifrate (le non-decifrabili restano per un retry successivo,
 *  es. arrivate prima del loro prekey). @returns {Promise<{received:number}>} */
const FAIL_BUDGET = 5; // giri di poll prima di scartare una busta non-decifrabile

export async function receiveSealed({ boxKeypair }) {
  const items = await fetchSealedMessages();
  if (!items.length) return { received: 0 };
  const knownPeers = await listSealedPeers();
  const acked = [];   // decifrate
  const purged = [];  // spazzatura/replay oltre budget
  let received = 0;
  for (const it of items) {
    try {
      const opened = await openSealedAny({ boxKeypair, msg: it.envelope, knownPeers });
      await addSealedMessage(opened.from, { id: it.id, mine: false, text: opened.text, ts: it.createdAt || Date.now(), stt: opened.ttl });
      if (!knownPeers.includes(opened.from)) knownPeers.push(opened.from); // peer nuovo scoperto
      received++;
      acked.push(it.id);
    } catch {
      // Non decifrabile ORA: può arrivare prima del suo prekey, oppure essere
      // spazzatura/replay. Dopo FAIL_BUDGET giri la cancelliamo (anti-accumulo /
      // anti-DoS: evita che la spazzatura blocchi la coda di fetch).
      const n = await bumpSealedFail(it.id);
      if (n >= FAIL_BUDGET) purged.push(it.id);
    }
  }
  const toAck = [...acked, ...purged];
  if (toAck.length) { await ackSealedMessages(toAck); await clearSealedFails(toAck); }
  return { received };
}
